import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { message } from 'antd';
import { BrowserRouter as Router } from 'react-router-dom';
import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { ThemeProvider } from 'styled-components';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import './App.less';
import { Routes } from './app/Routes';
import EntityRegistry from './app/entity/EntityRegistry';
import { DashboardEntity } from './app/entity/dashboard/DashboardEntity';
import { ChartEntity } from './app/entity/chart/ChartEntity';
import { UserEntity } from './app/entity/user/User';
import { GroupEntity } from './app/entity/group/Group';
import { DatasetEntity } from './app/entity/dataset/DatasetEntity';
import { DataFlowEntity } from './app/entity/dataFlow/DataFlowEntity';
import { DataJobEntity } from './app/entity/dataJob/DataJobEntity';
import { TagEntity } from './app/entity/tag/Tag';
import { EntityRegistryContext } from './entityRegistryContext';
import { Theme } from './conf/theme/types';
import defaultThemeConfig from './conf/theme/theme_light.config.json';
import { PageRoutes } from './conf/Global';
import { isLoggedInVar } from './app/auth/checkAuthStatus';
import { GlobalCfg } from './conf';
import { GlossaryTermEntity } from './app/entity/glossaryTerm/GlossaryTermEntity';
import { MLFeatureEntity } from './app/entity/mlFeature/MLFeatureEntity';
import { MLPrimaryKeyEntity } from './app/entity/mlPrimaryKey/MLPrimaryKeyEntity';
import { MLFeatureTableEntity } from './app/entity/mlFeatureTable/MLFeatureTableEntity';
import { MLModelEntity } from './app/entity/mlModel/MLModelEntity';
import { MLModelGroupEntity } from './app/entity/mlModelGroup/MLModelGroupEntity';
import { DomainEntity } from './app/entity/domain/DomainEntity';
import { ContainerEntity } from './app/entity/container/ContainerEntity';
import GlossaryNodeEntity from './app/entity/glossaryNode/GlossaryNodeEntity';
import { DataPlatformEntity } from './app/entity/dataPlatform/DataPlatformEntity';
import { DataProductEntity } from './app/entity/dataProduct/DataProductEntity';
import { DataPlatformInstanceEntity } from './app/entity/dataPlatformInstance/DataPlatformInstanceEntity';
import { RoleEntity } from './app/entity/Access/RoleEntity';
import AccessibilityHandler from './AccessibilityHandler';
import { setupLinkHandlerObservers } from './contextMenu';

/*
    Construct Apollo Client
*/
const httpLink = createHttpLink({ uri: '/api/v2/graphql' });

/**
 * Checks if the error is related to the 'getPrivaciTabProperties' operation and has a 404 status code.
 * @returns {boolean} - Returns true if the error is related to 'getPrivaciTabProperties' and has a 404 status code, otherwise false.
 */
const isAssetMapError = (error: any, extensions: any): boolean => {
    return error?.operation.operationName === 'getPrivaciTabProperties' && extensions?.code === 404;
};

/**
 * Handles GraphQL and network errors.
 * - For network errors with a 401 status code, logs out the user and redirects to the authentication page.
 * - For GraphQL errors, displays an error message unless the error is related to 'getPrivaciTabProperties' with a 404 status code.
 */
const errorLink = onError((error) => {
    const { networkError, graphQLErrors } = error;
    if (networkError) {
        const serverError = networkError as ServerError;
        if (serverError.statusCode === 401) {
            isLoggedInVar(false);
            Cookies.remove(GlobalCfg.CLIENT_AUTH_COOKIE);
            // Before redirecting to /authenticate route, we need to clear the local storage
            localStorage.clear();

            const currentPath = window.location.pathname + window.location.search;
            window.location.replace(`${PageRoutes.AUTHENTICATE}?redirect_uri=${encodeURIComponent(currentPath)}`);
        }
    }
    if (graphQLErrors && graphQLErrors.length) {
        const firstError = graphQLErrors[0];
        const { extensions } = firstError;
        const errorCode = extensions && (extensions.code as number);

        if (!isAssetMapError(error, extensions)) {
            // Fallback in case the calling component does not handle.
            message.error(`${firstError.message} (code ${errorCode})`, 3);
        }
    }
});

const withXSRFToken = setContext((_, { headers }) => {
    // get the xsrf token from local storage if it exists
    const xsrf = Cookies.get(GlobalCfg.XSRF_TOKEN);

    const xsrfParts = xsrf?.split('|') || [];
    const xsrfHeader = atob(xsrfParts[0]) || '';
    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            'X-EDC-XSRF-TOKEN': xsrfHeader,
        },
    };
});

const authFlowLink = withXSRFToken.concat(errorLink);

const client = new ApolloClient({
    connectToDevTools: true,
    link: authFlowLink.concat(httpLink),
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    dataset: {
                        merge: (oldObj, newObj) => {
                            return { ...oldObj, ...newObj };
                        },
                    },
                },
            },
        },
    }),
    credentials: 'include',
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'no-cache',
        },
        query: {
            fetchPolicy: 'no-cache',
        },
    },
});

const App: React.VFC = () => {
    const [dynamicThemeConfig, setDynamicThemeConfig] = useState<Theme>(defaultThemeConfig);

    useEffect(() => {
        import(`./conf/theme/${process.env.REACT_APP_THEME_CONFIG}`).then((theme) => {
            setDynamicThemeConfig(theme);
        });
    }, []);

    const entityRegistry = useMemo(() => {
        const register = new EntityRegistry();
        register.register(new DatasetEntity());
        register.register(new DashboardEntity());
        register.register(new ChartEntity());
        register.register(new UserEntity());
        register.register(new GroupEntity());
        register.register(new TagEntity());
        register.register(new DataFlowEntity());
        register.register(new DataJobEntity());
        register.register(new GlossaryTermEntity());
        register.register(new MLFeatureEntity());
        register.register(new MLPrimaryKeyEntity());
        register.register(new MLFeatureTableEntity());
        register.register(new MLModelEntity());
        register.register(new MLModelGroupEntity());
        register.register(new DomainEntity());
        register.register(new ContainerEntity());
        register.register(new GlossaryNodeEntity());
        register.register(new RoleEntity());
        register.register(new DataPlatformEntity());
        register.register(new DataProductEntity());
        register.register(new DataPlatformInstanceEntity());
        return register;
    }, []);

    /**
     * Set up link handler observers when the component mounts
     */
    useEffect(() => {
        // Set up the link handler observers and get the cleanup function
        const cleanupObservers = setupLinkHandlerObservers();

        // Return the cleanup function to be called when the component unmounts
        return cleanupObservers;
    }, []);

    return (
        <HelmetProvider>
            <ThemeProvider theme={dynamicThemeConfig}>
                <Router>
                    <Helmet>
                        <title>{dynamicThemeConfig.content.title}</title>
                    </Helmet>
                    <EntityRegistryContext.Provider value={entityRegistry}>
                        <ApolloProvider client={client}>
                            <AccessibilityHandler>
                                <Routes />
                            </AccessibilityHandler>
                        </ApolloProvider>
                    </EntityRegistryContext.Provider>
                </Router>
            </ThemeProvider>
        </HelmetProvider>
    );
};

export default App;
